"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useAuth } from "@/hooks/use-auth"
import { usePermissions } from "@/hooks/use-permissions"
import { extractUsersErrorMessage, usersQueryKey, type User } from "@/hooks/queries/users"

type CreateUserInput = Omit<
  User,
  "id" | "uuid" | "created_at" | "updated_at" | "active" | "additional_permissions" | "invite_token" | "invite_expires"
> & { password: string }

export function useCreateUserMutation() {
  return useUserMutation({
    request: async (fetchWithAuth, userData: CreateUserInput) => {
      const response = await fetchWithAuth("/api/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(userData),
      })
      if (!response.ok) throw new Error(await extractUsersErrorMessage(response, "Failed to create user"))
      return ((await response.json()) as { user: User }).user
    },
  })
}

export function useUpdateUserMutation() {
  return useUserMutation({
    request: async (fetchWithAuth, { uuid, userData }: { uuid: string; userData: Partial<User> & { password?: string } }) => {
      const response = await fetchWithAuth(`/api/users/${uuid}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(userData),
      })
      if (!response.ok) throw new Error(await extractUsersErrorMessage(response, "Failed to update user"))
      return ((await response.json()) as { user: User }).user
    },
  })
}

export function useDeleteUserMutation() {
  return useUserMutation({
    request: async (fetchWithAuth, uuid: string) => {
      const response = await fetchWithAuth(`/api/users/${uuid}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      })
      if (!response.ok) throw new Error(await extractUsersErrorMessage(response, "Failed to delete user"))
    },
  })
}

export function useUpdateUserPermissionsMutation() {
  return useUserMutation({
    request: async (fetchWithAuth, { uuid, additionalPermissions, disabledPermissions }: {
      uuid: string
      additionalPermissions: string[]
      disabledPermissions?: string[]
    }) => {
      const response = await fetchWithAuth(`/api/users/${uuid}/permissions`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          additional_permissions: additionalPermissions,
          disabled_permissions: disabledPermissions ?? [],
        }),
      })
      if (!response.ok) throw new Error(await extractUsersErrorMessage(response, "Failed to update permissions"))
      return ((await response.json()) as { user: User }).user
    },
  })
}

function useUserMutation<TInput, TResult>({
  request,
}: {
  request: (fetchWithAuth: ReturnType<typeof useAuth>["fetchWithAuth"], input: TInput) => Promise<TResult>
}) {
  const { fetchWithAuth } = useAuth()
  const { activeTenantId } = usePermissions()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: TInput) => request(fetchWithAuth, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: usersQueryKey(activeTenantId) }),
  })
}
